import React from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import {
  TrackChanges as RadarIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import StarBg from "../../../../../static/Images/Stars.svg";
import StarBg1 from "../../../../../static/Images/Stars1.png";

// Missile tier assets
import HypersonicImg from "../../../../../static/Images/Hypersonic.png";
import BallasticImg from "../../../../../static/Images/Ballastic.png";
import CruisImg from "../../../../../static/Images/cruis.png";
import InitiationImg from "../../../../../static/Images/Initiation.png";

export const MISSILE_TIERS = {
  1: {
    level: 1,
    name: "Initiation Missile",
    img: InitiationImg,
    badge: "Initiation Tier (Level 1)",
    accentColor: colors.emerald[500],
    bgColor: colors.slate[900],
    bgStars: StarBg1,
    desc: "Stage 1 starter trajectory for fast on-chain launch.",
  },
  2: {
    level: 2,
    name: "Cruise Missile",
    img: CruisImg,
    badge: "Cruise Tier (Level 2)",
    accentColor: colors.blue[500],
    bgColor: colors.slate[900],
    bgStars: StarBg1,
    desc: "Stage 2 advanced guided missile with increased rollover rewards.",
  },
  3: {
    level: 3,
    name: "Ballistic Missile",
    img: BallasticImg,
    badge: "Ballistic Tier (Level 3)",
    accentColor: colors.red[500],
    bgColor: colors.slate[900],
    bgStars: StarBg,
    desc: "Stage 3 high-impact payload with amplified prize pools.",
  },
  4: {
    level: 4,
    name: "Hypersonic Missile",
    img: HypersonicImg,
    badge: "Hypersonic Tier (Level 4)",
    accentColor: colors.amber[500],
    bgColor: colors.slate[900],
    bgStars: StarBg,
    desc: "Apex hypersonic multi-stage propulsion with maximum jackpot.",
  },
};

const ALL_TIERS = [
  { level: 1, label: "Initiation", color: colors.emerald[500] },
  { level: 2, label: "Cruise", color: colors.blue[500] },
  { level: 3, label: "Ballistic", color: colors.red[500] },
  { level: 4, label: "Hypersonic", color: colors.amber[500] },
];

const MissileVisualStage = ({
  level = 1,
  selectedNumber = "",
  pickNumbers = 3,
}) => {
  const currentLevel = Math.min(4, Math.max(1, Number(level) || 1));
  const tierInfo = MISSILE_TIERS[currentLevel];
  const maxDigits = Math.max(1, Number(pickNumbers) || 3);

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.slate[800]}`,
        bgcolor: colors.slate[900],
        color: "#ffffff",
        overflow: "hidden",
        position: "relative",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.25)",
        mb: 3,
      }}
    >
      {/* Background Star Texture */}
      <Box
        component="img"
        src={tierInfo.bgStars}
        alt="Space Atmosphere"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: 0.45,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 }, position: "relative", zIndex: 1 }}>
        {/* Top Tier Info & Radar Lock Indicator */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: tierInfo.accentColor,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                display: "block",
              }}
            >
              {tierInfo.badge}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#ffffff" }}>
              {tierInfo.name}
            </Typography>
          </Box>

          {/* Live Radar Target Lock Status */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              bgcolor: "rgba(15, 23, 42, 0.75)",
              border: `1px solid ${colors.slate[700]}`,
              borderRadius: borderRadiusPx.pill,
              px: { xs: 1.5, sm: 2 },
              py: 0.75,
              backdropFilter: "blur(8px)",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <RadarIcon sx={{ fontSize: 18, color: tierInfo.accentColor, flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[300],
                wordBreak: "break-all",
                fontSize: maxDigits > 6 ? "0.72rem" : "0.75rem",
              }}
            >
              Target Lock:{" "}
              <strong
                style={{
                  color: "#ffffff",
                  letterSpacing: maxDigits > 5 ? "0.06em" : "0.1em",
                }}
              >
                {selectedNumber
                  ? selectedNumber.padEnd(maxDigits, " _ ")
                  : "UNSET"}
              </strong>
            </Typography>
          </Stack>
        </Stack>

        {/* Central Rocket Display Deck */}
        <Box
          sx={{
            py: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            minHeight: "260px",
          }}
        >
          {/* Subtle Propulsion Glow behind missile */}
          <Box
            sx={{
              position: "absolute",
              width: "180px",
              height: "180px",
              borderRadius: borderRadiusPx.circle,
              background: `radial-gradient(circle, ${tierInfo.accentColor}33 0%, transparent 70%)`,
              filter: "blur(20px)",
              pointerEvents: "none",
            }}
          />

          {/* Missile Graphic Asset */}
          <Box
            component="img"
            src={tierInfo.img}
            alt={tierInfo.name}
            sx={{
              maxHeight: { xs: 200, sm: 240, md: 270 },
              maxWidth: "100%",
              objectFit: "contain",
              filter: "drop-shadow(0 12px 24px rgba(0,0,0,0.6))",
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(1.03) translateY(-4px)",
              },
            }}
          />

          <Typography
            variant="caption"
            sx={{
              color: colors.slate[400],
              mt: 2,
              textAlign: "center",
              maxWidth: "380px",
            }}
          >
            {tierInfo.desc}
          </Typography>
        </Box>

        {/* Bottom Progressive Rollover Stepper */}
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${colors.slate[800]}`,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            {ALL_TIERS.map((tier) => {
              const isCurrent = tier.level === currentLevel;
              const isPast = tier.level < currentLevel;

              return (
                <Box
                  key={tier.level}
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    p: 1,
                    borderRadius: borderRadiusPx.lg,
                    bgcolor: isCurrent
                      ? "rgba(255, 255, 255, 0.08)"
                      : "transparent",
                    border: isCurrent
                      ? `1px solid ${tier.color}`
                      : "1px solid transparent",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={0.5}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: borderRadiusPx.circle,
                        bgcolor: isCurrent || isPast ? tier.color : colors.slate[700],
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: isCurrent ? 700 : 500,
                        color: isCurrent
                          ? "#ffffff"
                          : isPast
                          ? colors.slate[300]
                          : colors.slate[500],
                        display: { xs: "none", sm: "inline" },
                      }}
                    >
                      {tier.label}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MissileVisualStage;
